import { useState, useEffect, useCallback } from 'react';
import { parseBlocks, serializeBlocks } from '../services/block-utils';
import { diff3Blocks } from '../services/diff3';
import { fileConflictDetail, resolveExternalConflict, fileSaveContent } from '../services/api';
import { outboxGet, outboxRemove } from '../services/outbox';
import BottomPanel from './BottomPanel';

/**
 * Per-file conflict resolve view.
 *
 * Loads the three-way inputs (base / ours / theirs), runs a block-level diff3,
 * and lets the user pick "use all local", "use all server", or resolve each
 * conflicting block individually (keep local / use server / keep both / manual).
 *
 * Props:
 *  - conflict    : { path, type: 'device' | 'external' }
 *  - onBack      : () => void — back to the conflict inbox
 *  - onResolved  : (path) => void — conflict resolved; refresh the inbox
 *  - toast       : (type, message, duration?) => void
 */

export default function ConflictResolveView({ conflict, onBack, onResolved, toast }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null); // { base, ours, theirs, remoteSha, isTwoway }
  const [segments, setSegments] = useState(null); // diff3 segments (threeway)
  const [autoMerged, setAutoMerged] = useState(0);
  const [resolutions, setResolutions] = useState([]); // per-conflict resolution
  const [manualTexts, setManualTexts] = useState([]); // per-conflict manual edit text
  const [editingConflict, setEditingConflict] = useState(null); // index open in the panel
  const [saving, setSaving] = useState(false);

  const path = conflict?.path;
  const type = conflict?.type;

  // Load the three-way inputs for this conflict.
  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    setError('');
    try {
      let base, ours, theirs, remoteSha, timeline;
      if (type === 'device') {
        const entry = await outboxGet(path);
        if (!entry) throw new Error('This conflict is no longer available.');
        base = entry.baseContent ?? null;
        ours = entry.content ?? '';
        theirs = entry.remoteContent ?? '';
        remoteSha = entry.remoteSha ?? null;
        timeline = Array.isArray(entry.timeline) ? entry.timeline : null;
      } else {
        const detail = await fileConflictDetail(path);
        base = detail.base_content ?? null;
        ours = detail.ours_content ?? '';
        theirs = detail.theirs_content ?? '';
        remoteSha = detail.remote_blob_sha ?? null;
      }

      const isTwoway = base == null;
      let segs = null;
      let autoCount = 0;
      let res = [];
      let mt = [];

      if (isTwoway) {
        // No ancestor → degenerate two-way (pick any).
        res = [null];
        mt = [''];
      } else {
        const result = diff3Blocks(parseBlocks(base), parseBlocks(ours), parseBlocks(theirs));
        segs = result.segments;
        autoCount = result.autoMergedCount;
        const conflictCount = segs.filter((s) => s.type === 'conflict').length;
        res = new Array(conflictCount).fill(null);
        mt = new Array(conflictCount).fill('');
      }

      setData({ base, ours, theirs, remoteSha, isTwoway, timeline });
      setSegments(segs);
      setAutoMerged(autoCount);
      setResolutions(res);
      setManualTexts(mt);
    } catch (e) {
      setError(e.message || 'Failed to load conflict');
    } finally {
      setLoading(false);
    }
  }, [path, type]);

  useEffect(() => { load(); }, [load]);

  // Build the final merged content from the current resolutions.
  const buildFinalContent = useCallback(() => {
    if (!data) return '';
    if (data.isTwoway) {
      const r = resolutions[0];
      if (r === 'theirs') return data.theirs;
      if (r === 'manual') return manualTexts[0] ?? data.ours;
      return data.ours; // default "use all local"
    }
    const blocks = [];
    let ci = 0;
    for (const seg of segments || []) {
      if (seg.type === 'auto') {
        blocks.push(...seg.blocks);
      } else {
        const r = resolutions[ci];
        if (r === 'theirs') blocks.push(...seg.theirs);
        else if (r === 'both') { blocks.push(...seg.ours); blocks.push(...seg.theirs); }
        else if (r === 'manual') { const t = (manualTexts[ci] ?? '').trim(); if (t) blocks.push(t); }
        else blocks.push(...seg.ours);
        ci++;
      }
    }
    return serializeBlocks(blocks);
  }, [data, segments, resolutions, manualTexts]);

  const applyResolution = (ci, kind) => {
    setResolutions((prev) => {
      const next = prev.slice();
      next[ci] = kind;
      return next;
    });
  };

  const setManualText = (ci, text) => {
    setManualTexts((prev) => {
      const next = prev.slice();
      next[ci] = text;
      return next;
    });
  };

  // Write the merged result back.
  const handleSave = useCallback(async (mode) => {
    if (!data || saving) return;
    setSaving(true);
    try {
      let finalContent;
      if (mode === 'ours') {
        finalContent = data.ours;
      } else if (mode === 'theirs') {
        finalContent = data.theirs;
      } else {
        finalContent = buildFinalContent();
      }

      if (type === 'external') {
        await resolveExternalConflict(path, finalContent, data.remoteSha);
      } else {
        // Preserve any pending timeline marks and re-attach them to the
        // resolving save — a conflict must never silently drop a mark.
        await fileSaveContent(path, finalContent, data.remoteSha, null, false, data.timeline);
      }

      // Clean up any local outbox entry for the resolved file.
      await outboxRemove(path);

      onResolved(path);
    } catch (err) {
      if (err && err.status === 409) {
        toast('warning', 'File changed again during resolution — reloading…', 4000);
        await load();
      } else {
        toast('error', err.message || 'Failed to resolve conflict', 5000);
      }
    } finally {
      setSaving(false);
    }
  }, [data, saving, type, path, buildFinalContent, load, onResolved, toast]);

  if (!path) return null;

  const conflictSegments = (segments || []).filter((s) => s.type === 'conflict');

  return (
    <div className="conflict-resolve">
      <div className="conflict-resolve-header">
        <button className="btn" onClick={onBack} title="Back to conflicts">&larr;</button>
        <span className="conflict-resolve-title">{path}</span>
      </div>

      {loading ? (
        <div className="conflict-resolve-empty">
          <span className="spinner" />
          <div>Loading conflict…</div>
        </div>
      ) : error ? (
        <div className="conflict-resolve-empty">
          <div style={{ color: 'var(--danger)' }}>{error}</div>
        </div>
      ) : (
        <>
          {/* Shortcuts */}
          <div className="conflict-resolve-shortcuts">
            <button className="btn btn-primary" onClick={() => handleSave('ours')} disabled={saving}>
              Use all local
            </button>
            <button className="btn" onClick={() => handleSave('theirs')} disabled={saving}>
              Use all server
            </button>
          </div>

          {data.isTwoway ? (
            <div className="conflict-resolve-scroll">
              <div className="conflict-resolve-twoway">
                <div className="conflict-resolve-twoway-col">
                  <div className="conflict-resolve-twoway-label">Local (this device)</div>
                  <div className="conflict-resolve-twoway-body">{data.ours || '(empty)'}</div>
                </div>
                <div className="conflict-resolve-twoway-col">
                  <div className="conflict-resolve-twoway-label">Server</div>
                  <div className="conflict-resolve-twoway-body">{data.theirs || '(empty)'}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="conflict-resolve-scroll">
              {autoMerged > 0 && (
                <div className="conflict-resolve-summary">
                  Auto-merged {autoMerged} change{autoMerged === 1 ? '' : 's'}.
                </div>
              )}
              {conflictSegments.length === 0 ? (
                <div className="conflict-resolve-empty">No conflicts — everything auto-merged.</div>
              ) : (
                conflictSegments.map((seg, i) => {
                  const oursText = seg.ours.join('\n\n');
                  const theirsText = seg.theirs.join('\n\n');
                  const res = resolutions[i];
                  return (
                    <div key={i} className="conflict-block">
                      <div className="conflict-block-label conflict-block-label--ours">Local</div>
                      <div className="conflict-block-body">{oursText || '(empty)'}</div>
                      <div className="conflict-block-divider" />
                      <div className="conflict-block-label conflict-block-label--theirs">Server</div>
                      <div className="conflict-block-body">{theirsText || '(empty)'}</div>
                      <div className="conflict-block-actions">
                        <button
                          className={`btn${res === 'ours' ? ' btn-primary' : ''}`}
                          onClick={() => applyResolution(i, 'ours')}
                        >
                          Keep local
                        </button>
                        <button
                          className={`btn${res === 'theirs' ? ' btn-primary' : ''}`}
                          onClick={() => applyResolution(i, 'theirs')}
                        >
                          Use server
                        </button>
                        <button
                          className={`btn${res === 'both' ? ' btn-primary' : ''}`}
                          onClick={() => applyResolution(i, 'both')}
                        >
                          Keep both
                        </button>
                        <button
                          className={`btn${res === 'manual' ? ' btn-primary' : ''}`}
                          onClick={() => { applyResolution(i, 'manual'); setEditingConflict(i); }}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          <div className="conflict-resolve-footer">
            <button className="btn" onClick={onBack}>Cancel</button>
            <button className="btn btn-primary" onClick={() => handleSave('merge')} disabled={saving}>
              {saving ? 'Saving…' : 'Save merged'}
            </button>
          </div>

          {/* Manual edit — bottom panel (diff reference kept above the textarea) */}
          <BottomPanel
            show={editingConflict !== null}
            title={`Edit conflict ${(editingConflict ?? 0) + 1}`}
            onClose={() => setEditingConflict(null)}
            footer={
              <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
                <button className="btn" onClick={() => setEditingConflict(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => setEditingConflict(null)}>Save</button>
              </div>
            }
          >
            {editingConflict !== null && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', padding: 'var(--space-md)' }}>
                <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                  <div className="conflict-block">
                    <div className="conflict-block-label conflict-block-label--ours">Local</div>
                    <div className="conflict-block-body">{(conflictSegments[editingConflict]?.ours || []).join('\n\n') || '(empty)'}</div>
                    <div className="conflict-block-divider" />
                    <div className="conflict-block-label conflict-block-label--theirs">Server</div>
                    <div className="conflict-block-body">{(conflictSegments[editingConflict]?.theirs || []).join('\n\n') || '(empty)'}</div>
                  </div>
                </div>
                <textarea
                  className="conflict-block-edit"
                  value={manualTexts[editingConflict] ?? ''}
                  onChange={(e) => setManualText(editingConflict, e.target.value)}
                  placeholder="Write the merged block…"
                  style={{ minHeight: 120 }}
                />
              </div>
            )}
          </BottomPanel>
        </>
      )}
    </div>
  );
}
